import { useMutation } from '@tanstack/react-query';
import { API } from '../../utils/amplifyWrapper';
import clean from '../../utils/Clean';
import only from '../../utils/Only';
import { useWebSocketStore } from '../../zustand-store/useWebSocketStore';

interface IAudioPreviewProps {
    profile: any;
    text?: string;
}

const DEFAULT_PREVIEW_TEXT = "If you set your goals ridiculously high and it's a failure, you will fail above everyone else's success.";

// Matches the backend's documented estimate ("10-30 seconds") plus headroom.
const ASYNC_PREVIEW_TIMEOUT_MS = 45000;

/**
 * Hook to generate audio preview for a voice profile
 * Uses React Query mutation for better state management
 */
export const useAudioPreview = () => {
    const getConnectionId = useWebSocketStore((state) => state.getConnectionId);
    const subscribeToMessages = useWebSocketStore((state) => state.subscribeToMessages);

    return useMutation(
        async ({ profile, text }: IAudioPreviewProps) => {
            const SAMPLE = [
                {
                    type: "paragraph",
                    children: [
                        {
                            text: text || DEFAULT_PREVIEW_TEXT
                        }
                    ]
                }
            ];

            // Emotional voices generate asynchronously over the websocket, so the
            // backend needs a connection id to push the result back to this client.
            let socketConnectionId: string | null = null;
            if (profile.engine === 'emotional') {
                socketConnectionId = await getConnectionId();
                if (!socketConnectionId) {
                    throw new Error('Connection issue. Please try again.');
                }
            }

            const response = await API.post('main', `/audio/preview`, {
                body: {
                    slate_value: SAMPLE,
                    ...clean(only({
                        ...profile,
                        ...(profile.volume && { volume: parseFloat(profile.volume) }),
                        ...(profile.speed && { speed: parseInt(profile.speed) }),
                        ...(socketConnectionId && { socketConnectionId }),
                    }, ['lang_code', 'platform', 'engine', 'voice', 'volume', 'speed', 'style', 'pitch', 'socketConnectionId', 'audio', 'vox_ref_audio', 'metadata', 'qwen_pt_path']))
                },
                headers: { 'Content-Type': 'application/json' }
            });

            // Emotional voices don't return audio inline — the backend responds
            // immediately and pushes the real result over the websocket once ready,
            // keyed by the blockHash it hands back here.
            if (response.async) {
                if (!response.blockHash) {
                    throw new Error('Audio generation started but no tracking id was returned.');
                }

                return new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        unsubscribe();
                        reject(new Error('Audio generation timed out. Please try again.'));
                    }, ASYNC_PREVIEW_TIMEOUT_MS);

                    const unsubscribe = subscribeToMessages(response.blockHash, (message: any) => {
                        // The Lambda also emits a progress ping (shared with the emotion-prompt
                        // improvement flow) before the real result — ignore it and keep waiting.
                        if (message.type === 'audio_generation_started') return;

                        clearTimeout(timeout);
                        unsubscribe();

                        if (message.type === 'audio_generation_error' || message.error) {
                            reject(new Error(message.message || message.error || 'Failed to generate audio.'));
                            return;
                        }

                        // audio_url arrives as a bare S3 key (same convention as every
                        // other audio_generation_complete producer) — prepend the temp
                        // bucket's base URL to make it playable.
                        resolve({
                            data: {
                                ...message.data,
                                audio_url: `${process.env.REACT_APP_TEMP_AUDIO_BASE_URL}/${message.data.audio_url}`,
                            },
                        });
                    });
                });
            }

            return response;
        }
    );
};

